import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { AppBottomSheet, Button, Text } from '~/components/core';
import { Ionicons } from '~/components/core/Icons';
import { formatNumberGrouped } from '~/lib/format';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { FONT_SIZE_L, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { resultHeadline, resultSummaryLine, StrategyResultView, withCurrency } from '../../utils/results';

type Props = {
    results: StrategyResultView;
    busy?: boolean;
    onContinue: () => void;
    onDiscard: () => void;
};

// The intermediate reveal for `done` (not-yet-seen) strategies: the optimization just finished
// and the user gets the headline number before anything else. Continue fires markSeen (done ->
// seen per the lifecycle) and drops into the regular decision view; Discard rejects the result
// without ever entering it. Detail tabs are deliberately absent here — this screen is the hook,
// not the analysis.
export default function ResultReveal({ results, busy, onContinue, onDiscard }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const [explaining, setExplaining] = useState<'return' | 'funds' | null>(null);
    const summaryLine = resultSummaryLine(results);

    // The capital the strategy needs to run, and what it made on it — the two figures that give
    // the headline percentage meaning. Both come from backend_json, so both can be absent.
    const requiredFunds =
        typeof results.requiredFunds === 'number'
            ? withCurrency(formatNumberGrouped(results.requiredFunds, 2), results.accountCurrency)
            : null;
    const hasMoney = typeof results.resultAccount === 'number';
    const resultTotal = hasMoney
        ? withCurrency(
              `${results.resultAccount >= 0 ? '+' : ''}${formatNumberGrouped(results.resultAccount, 2)}`,
              results.accountCurrency
          )
        : null;

    // The projected profit in money is the hook; lead with it and demote the return percentage to
    // a supporting line. When backend_json never computed (no money, no percentage), the headline
    // degrades to the percentage helper's "—" and keeps the info affordance where it was.
    const hero = resultTotal ?? resultHeadline(results);
    // A losing backtest turns the headline and the return chip red; a winner keeps the brand teal.
    const isLoss = hasMoney && results.resultAccount < 0;
    const accentColor = isLoss ? theme.alert : theme.button;
    const returnText =
        typeof results.resultPercentage === 'number'
            ? `${results.resultPercentage >= 0 ? '+' : ''}${formatNumberGrouped(results.resultPercentage, 2)}% ${i18n.t(
                  'ai-strategy-reveal-return-word'
              )}`
            : null;
    const supportLine = [
        hasMoney ? returnText : null,
        requiredFunds ? `${i18n.t('ai-strategy-required-funds')} ${requiredFunds}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                <View style={styles.iconCircle}>
                    <Ionicons name="sparkles-outline" size={scaleSize(26)} style={styles.icon} />
                </View>
                <Text fontType="LATO_BOLD" style={styles.title}>
                    ai-strategy-reveal-title
                </Text>
                <Text style={styles.subtitle}>ai-strategy-reveal-subtitle</Text>
                <Text style={styles.caption}>
                    {hasMoney ? i18n.t('ai-strategy-reveal-profit') : i18n.t('ai-strategy-decision-return')}
                </Text>
                {hasMoney ? (
                    <Text
                        noTranslate
                        fontType="LATO_BOLD"
                        style={[styles.headline, { color: accentColor }]}
                        numberOfLines={1}
                        adjustsFontSizeToFit
                    >
                        {hero}
                    </Text>
                ) : (
                    <Pressable
                        style={({ pressed }) => [styles.headlineRow, pressed && styles.pressed]}
                        onPress={() => setExplaining('return')}
                        hitSlop={8}
                        accessibilityLabel={`${i18n.t('ai-strategy-decision-return')} info`}
                    >
                        <Text noTranslate fontType="LATO_BOLD" style={styles.headline}>
                            {hero}
                        </Text>
                        <Ionicons name="information-circle-outline" size={scaleSize(17)} style={styles.infoIcon} />
                    </Pressable>
                )}
                {hasMoney ? (
                    returnText || requiredFunds ? (
                        <View style={styles.chipRow}>
                            {returnText ? (
                                <Pressable
                                    onPress={() => setExplaining('return')}
                                    hitSlop={6}
                                    style={({ pressed }) => [
                                        styles.chip,
                                        { borderColor: accentColor },
                                        pressed && styles.pressed,
                                    ]}
                                    accessibilityLabel={`${i18n.t('ai-strategy-decision-return')} info`}
                                >
                                    <Text noTranslate style={[styles.chipText, { color: accentColor }]}>
                                        {returnText}
                                    </Text>
                                    <Ionicons
                                        name="information-circle-outline"
                                        size={scaleSize(14)}
                                        style={[styles.chipInfoIcon, { color: accentColor }]}
                                    />
                                </Pressable>
                            ) : null}
                            {requiredFunds ? (
                                <Pressable
                                    onPress={() => setExplaining('funds')}
                                    hitSlop={6}
                                    style={({ pressed }) => [
                                        styles.chip,
                                        styles.chipNeutral,
                                        pressed && styles.pressed,
                                    ]}
                                    accessibilityLabel={`${i18n.t('ai-strategy-required-funds')} info`}
                                >
                                    <Text noTranslate style={styles.chipTextNeutral}>
                                        {`${i18n.t('ai-strategy-reveal-on')} ${requiredFunds}`}
                                    </Text>
                                    <Ionicons
                                        name="information-circle-outline"
                                        size={scaleSize(14)}
                                        style={[styles.chipInfoIcon, styles.chipIconNeutral]}
                                    />
                                </Pressable>
                            ) : null}
                        </View>
                    ) : null
                ) : supportLine ? (
                    <Text noTranslate style={styles.summaryLine}>
                        {supportLine}
                    </Text>
                ) : null}
                <Text noTranslate style={styles.summaryLine}>
                    {summaryLine}
                </Text>
            </View>

            <View style={styles.buttonsRow}>
                <View style={styles.buttonWrap}>
                    <Button
                        title="ai-strategy-action-discard"
                        onPress={onDiscard}
                        disabled={busy}
                        backgroundColor={{ backgroundColor: theme.backgroundSecondary }}
                        titleColor={theme.text}
                    />
                </View>
                <View style={styles.buttonWrap}>
                    <Button title="ai-strategy-reveal-continue" onPress={onContinue} disabled={busy} />
                </View>
            </View>

            <AppBottomSheet
                visible={explaining !== null}
                onClose={() => setExplaining(null)}
                title={i18n.t(explaining === 'funds' ? 'ai-strategy-required-funds' : 'ai-strategy-decision-return')}
            >
                <Text style={styles.explainerText}>
                    {explaining === 'funds' ? 'ai-strategy-required-funds-desc' : 'ai-strategy-decision-return-desc'}
                </Text>
            </AppBottomSheet>
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flex: 1,
            paddingHorizontal: scaleSize(24),
            paddingBottom: scaleSize(16),
        },
        content: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
        },
        iconCircle: {
            width: scaleSize(56),
            height: scaleSize(56),
            borderRadius: scaleSize(28),
            borderWidth: 2,
            borderColor: theme.button,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: scaleSize(16),
        },
        icon: {
            color: theme.button,
        },
        title: {
            fontSize: FONT_SIZE_L,
            color: theme.text,
            textAlign: 'center',
        },
        subtitle: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            textAlign: 'center',
            marginTop: scaleSize(4),
            marginBottom: scaleSize(24),
        },
        caption: {
            fontSize: FONT_SIZE_L,
            color: theme.textSecondary,
        },
        headlineRow: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            marginTop: scaleSize(2),
        },
        pressed: {
            opacity: 0.6,
        },
        headline: {
            fontSize: scaleSize(44),
            lineHeight: scaleSize(52),
            color: theme.button,
        },
        infoIcon: {
            color: theme.textSecondary,
            marginTop: scaleSize(6),
            marginLeft: scaleSize(4),
        },
        chipRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: scaleSize(10),
        },
        chip: {
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderRadius: scaleSize(10),
            paddingVertical: scaleSize(3),
            paddingHorizontal: scaleSize(8),
            marginHorizontal: scaleSize(3),
            marginTop: scaleSize(4),
        },
        chipNeutral: {
            borderColor: theme.tabIconDefault,
        },
        chipText: {
            fontSize: FONT_SIZE_S,
        },
        chipTextNeutral: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
        },
        chipInfoIcon: {
            marginLeft: scaleSize(4),
        },
        chipIconNeutral: {
            color: theme.textSecondary,
        },
        summaryLine: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(6),
            textAlign: 'center',
        },
        buttonsRow: {
            flexDirection: 'row',
            alignSelf: 'stretch',
        },
        buttonWrap: {
            flex: 1,
            marginHorizontal: scaleSize(4),
        },
        explainerText: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            lineHeight: scaleSize(20),
            paddingBottom: scaleSize(10),
        },
    });
